import { tt } from '@agos/shared'

/**
 * The diagnostics console, and what each command actually does to the register.
 *
 * `POST /fiskal/raw` sends a string straight down the TCP socket to a certified
 * fiscal device. Legacy puts eight one-click preset buttons next to a free-text
 * box and treats them all alike — but they are not alike. Three of the eight
 * **change the device**: `FSLOC6466%1` switches card payments on (legacy's own
 * hint then tells you to restart the register), and `FTCARDSTART` opens a card
 * payment, which makes the terminal ask a customer to tap. Mixed in with them is
 * `FRSTAT`, which asks a question and changes nothing.
 *
 * So the commands are classified, and only the harmless ones stay one-click.
 *
 * **On the classification.** There is no vendor spec in this repo. The prefixes
 * are read off the command set legacy ships and the transport that uses them
 * (`fiskal-tcp.service.ts`, `FiskalDocument.java`), and they are consistent
 * across every command in both:
 *
 * | Prefix | Meaning | Evidence |
 * |---|---|---|
 * | `FR` | read a value | `FRSTAT` (stav kasy), `FRINFO` (konfig), `FRPAYINFO` (platidlá), `FRMODEINFO` (mód), `FRRTC` (clock), `FRLOC6466` (card on/off?) |
 * | `FS` | set a value | `FSLOC6466%1` (turn the card reader on), `FSIMPNR` (implementor id), `FSRST` (restart) |
 * | `FT` | a transaction | `FTOPEN`/`FITEM`/`FTCLOSE` build a document; `FTCARDSTART` charges a card; `FTUID` stamps one |
 * | `FX` | administrative | `FXEKASAUNBLOCK` |
 *
 * An unrecognised command is treated as **writing**, because the cost of being
 * wrong runs one way: a needless confirmation on a query costs a click, and a
 * missing one on a set command costs a device somebody has to drive out to.
 */

export type CommandEffect =
  /** Asks the device something. Safe to repeat. */
  | 'reads'
  /** Changes the device's configuration or state. */
  | 'writes'
  /** Opens or closes a document, or charges a card. */
  | 'transacts'

export interface DiagnosticCommand {
  cmd: string
  label: string
  effect: CommandEffect
  /** Shown on the confirmation, so the click is informed rather than brave. */
  consequence?: string
}

/** Legacy's eight presets, classified. Same commands, same order. */
export const DIAGNOSTIC_COMMANDS: readonly DiagnosticCommand[] = [
  { cmd: 'FRPAYINFO', label: tt('FRPAYINFO (platidlá)'), effect: 'reads' },
  { cmd: 'FRLOC6466', label: tt('FRLOC6466 (karta zapnutá?)'), effect: 'reads' },
  { cmd: 'FRSTAT', label: tt('FRSTAT (stav kasy)'), effect: 'reads' },
  { cmd: 'FRINFO', label: tt('FRINFO (konfigurácia DPH)'), effect: 'reads' },
  { cmd: 'FRMODEINFO', label: tt('FRMODEINFO (mód)'), effect: 'reads' },
  {
    cmd: 'FSLOC6466%1',
    label: tt('FSLOC6466%1 (zapnúť kartu)'),
    effect: 'writes',
    consequence: tt(
      'Zapne kartové platby v konfigurácii pokladne. Po tomto príkaze treba pokladňu restartovať (FSRST).',
    ),
  },
  { cmd: 'FTCARDINFO', label: tt('FTCARDINFO (stav platby)'), effect: 'reads' },
  {
    // Legacy builds this one with a fresh UUID *in the render body*, so every
    // render produced a different command. It belongs in the click.
    cmd: 'FTCARDSTART%1.00',
    label: tt('FTCARDSTART%1.00 (test platby)'),
    effect: 'transacts',
    consequence: tt(
      'Otvorí kartovú platbu na 1,00 € - terminál vyzve zákazníka priložiť kartu a výsledok príde cez FTCARDINFO.',
    ),
  },
] as const

const READ_PREFIX = 'FR'
const SET_PREFIX = 'FS'
const TRANSACTION_PREFIX = 'FT'

/** The `FT` commands that only stamp or query rather than move money. */
const HARMLESS_TRANSACTION_COMMANDS = new Set(['FTCARDINFO'])

/**
 * What a single command does. Commands are chained with commas, so the caller
 * folds the parts together.
 */
function effectOfPart(part: string): CommandEffect {
  const cmd = part.trim().toUpperCase()
  if (!cmd) return 'reads'
  if (HARMLESS_TRANSACTION_COMMANDS.has(cmd)) return 'reads'
  if (cmd.startsWith(READ_PREFIX)) return 'reads'
  if (cmd.startsWith(SET_PREFIX)) return 'writes'
  if (cmd.startsWith(TRANSACTION_PREFIX)) return 'transacts'
  return 'writes'
}

const SEVERITY: Record<CommandEffect, number> = { reads: 0, writes: 1, transacts: 2 }

/**
 * What a typed command does — the strongest effect of its parts.
 *
 * `FTUID…,FTCARDSTART%1.00` is two commands, and the UID stamp being harmless
 * does not make the charge harmless.
 */
export function effectOf(command: string): CommandEffect {
  const parts = command.split(',').filter((part) => part.trim() !== '')
  if (parts.length === 0) return 'reads'
  return parts.reduce<CommandEffect>(
    (strongest, part) => {
      const effect = effectOfPart(part)
      return SEVERITY[effect] > SEVERITY[strongest] ? effect : strongest
    },
    'reads',
  )
}

/** Anything that is not a plain question is confirmed first. */
export function needsConfirmation(command: string): boolean {
  return effectOf(command) !== 'reads'
}

/**
 * A UUID stamp for a command that opens a document.
 *
 * The device wants it without hyphens and in upper case. Called from a click,
 * never from a render — a value that changes every render is not a command.
 */
export function stampUid(command: string): string {
  const uid = crypto.randomUUID().toUpperCase().replaceAll('-', '')
  return `FTUID${uid},${command}`
}
